import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { reloadPage } from 'libs/utils/window';
import useBreakpoint from 'hooks/useBreakpoint';
import { getLanguages, updateLocale } from 'data/api';
import { getLocale } from 'state/intl/selectors';
import LanguageSelectorDropdown from './LanguageSelectorDropdown';
import LanguageSelectorModal from './LanguageSelectorModal';
import { jsx as _jsx } from "react/jsx-runtime";

const getActiveLanguage = (languages, locale) => {
  const currentLanguage = languages.find(language => language.current);
  const defaultLanguage = languages.find(language => language.code === locale);
  return currentLanguage || defaultLanguage;
};

const LanguageSelector = _ref => {
  let {
    isStandalone = false,
    initialLanguages = []
  } = _ref;
  const breakpoints = useBreakpoint();
  const locale = useSelector(getLocale);
  const activeLang = getActiveLanguage(initialLanguages, locale);
  const {
    0: languages,
    1: setLanguages
  } = useState(initialLanguages);
  const {
    0: selectedLanguage,
    1: setSelectedLanguage
  } = useState(activeLang || null);
  const {
    0: activeLanguage,
    1: setActiveLanguage
  } = useState(activeLang || null);
  const {
    0: isMenuOpen,
    1: setIsMenuOpen
  } = useState(false);
  useEffect(() => {
    const loadLanguages = async () => {
      if (languages.length) return;
      const response = await getLanguages();
      if ('errors' in response) return;
      const remoteActiveLanguage = getActiveLanguage(response.languages, locale);

      if (remoteActiveLanguage) {
        setActiveLanguage(remoteActiveLanguage);
        setSelectedLanguage(remoteActiveLanguage);
      }

      setLanguages(response.languages);
    };

    loadLanguages();
  }, [locale, languages]);

  const hideMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLanguageSelect = language => {
    setSelectedLanguage(language);
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(prevState => !prevState);
  };

  const handleMenuHide = () => {
    hideMenu();
  };

  const saveLanguageSelection = async language => {
    hideMenu();
    const response = await updateLocale(language.code);
    if (!response) return;
    setActiveLanguage(language);
    reloadPage();
  };

  if (languages.length <= 1 || !activeLanguage || !selectedLanguage) return null;

  if (breakpoints.portables) {
    return /*#__PURE__*/_jsx(LanguageSelectorModal, {
      languages: languages,
      activeLanguage: activeLanguage,
      isMenuOpen: isMenuOpen,
      isStandalone: isStandalone,
      onLanguageMenuToggle: handleMenuToggle,
      selectedLanguage: selectedLanguage,
      onLanguageSelect: handleLanguageSelect,
      saveLanguageSelection: saveLanguageSelection
    });
  }

  return /*#__PURE__*/_jsx(LanguageSelectorDropdown, {
    languages: languages,
    activeLanguage: activeLanguage,
    isMenuOpen: isMenuOpen,
    onLanguageMenuToggle: handleMenuToggle,
    hideLanguageMenu: handleMenuHide,
    saveLanguageSelection: saveLanguageSelection
  });
};

export default LanguageSelector;